import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Upload,
  message,
  Spin,
  Skeleton,
} from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useUpdateCategoryMutation } from "@/services/api/category/categoryApi";
import { uploadToCloudinary } from "@/utils/cloudinaryUpload";

const { TextArea } = Input;

/**
 * Edit Category Modal Component with Image Loading States
 */
const EditCategoryModal = ({ visible, onCancel, onSuccess, categoryData }) => {
  const [form] = Form.useForm();
  const [imageUrl, setImageUrl] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState("");

  const [updateCategory, { isLoading }] = useUpdateCategoryMutation();

  // Populate form and image preview when modal opens
  useEffect(() => {
    if (categoryData && visible) {
      form.setFieldsValue({
        name: categoryData.name,
        description: categoryData.description || "",
      });
      setImageUrl(categoryData.image || "");
      setImageFile(null);
      setImageLoading(!!categoryData.image); // Show loader if there's an image
      setImageError("");
    }
  }, [categoryData, visible, form]);

  // Handle image load
  const handleImageLoad = () => {
    setImageLoading(false);
  };

  // Handle image error
  const handleImageError = () => {
    setImageLoading(false);
    setImageError("Failed to load image");
  };

  // Handle manual image selection and preview
  const handleBeforeUpload = (file) => {
    const isImage = file.type.startsWith("image/");
    if (!isImage) {
      message.error("You can only upload image files!");
      return Upload.LIST_IGNORE;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error("Image must be smaller than 2MB!");
      return Upload.LIST_IGNORE;
    }

    setImageFile(file);
    setImageUrl(URL.createObjectURL(file));
    setImageLoading(false);
    setImageError("");
    return false;
  };

  // Remove image
  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setImageUrl("");
    setImageFile(null);
    setImageLoading(false);
    setImageError("Please upload a category image");
  };

  // Handle form submission
  const handleSubmit = async (values) => {
    if (!categoryData?.id) {
      message.error("Category ID is missing");
      return;
    }

    // Validate image - check for both null/undefined and empty string
    if (!imageFile && (!imageUrl || imageUrl.trim() === "")) {
      setImageError("Please upload a category image");
      message.error("Please upload a category image");
      return;
    }

    setIsSubmitting(true);
    try {
      let finalImageUrl = imageUrl;

      // Upload new image to Cloudinary if selected
      if (imageFile instanceof File) {
        message.loading("Uploading image...", 0);
        finalImageUrl = await uploadToCloudinary(imageFile);
        message.destroy();
        message.success("Image uploaded successfully!");
      }

      // Double-check before sending to API
      if (!finalImageUrl || finalImageUrl.trim() === "") {
        message.error("Please upload a category image");
        setImageError("Please upload a category image");
        setIsSubmitting(false);
        return;
      }

      const payload = {
        name: values.name,
        description: values.description || "",
        url: finalImageUrl,
        created_by: categoryData.created_by,
      };

      await updateCategory({
        id: categoryData.id,
        requestData: payload,
      }).unwrap();

      message.success("Category updated successfully!");
      form.resetFields();
      setImageUrl("");
      setImageFile(null);
      setImageError("");
      onSuccess && onSuccess();
      onCancel();
    } catch (error) {
      console.error("Error updating category:", error);
      // Filter out backend URL validation errors and show our custom message
      const errorMessage =
        error?.data?.message || error.message || "Failed to update category";
      if (
        errorMessage.toLowerCase().includes("url") ||
        errorMessage.toLowerCase().includes("string") ||
        errorMessage.toLowerCase().includes("image")
      ) {
        message.error("Please upload a category image");
        setImageError("Please upload a category image");
      } else {
        message.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal cancel
  const handleCancel = () => {
    form.resetFields();
    setImageUrl("");
    setImageFile(null);
    setImageError("");
    setImageLoading(false);
    onCancel();
  };

  if (!categoryData) return null;

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <EditOutlined className="text-orange-500" />
          <span>Edit Category</span>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      destroyOnClose
    >
      <Spin spinning={isSubmitting || isLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="mt-4"
        >
          <Form.Item
            name="name"
            label="Category Name"
            rules={[
              { required: true, message: "Please enter category name" },
              { min: 2, message: "Name must be at least 2 characters" },
              { max: 100, message: "Name must not exceed 100 characters" },
            ]}
          >
            <Input placeholder="Enter category name" size="large" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[
              {
                max: 500,
                message: "Description must not exceed 500 characters",
              },
            ]}
          >
            <TextArea
              placeholder="Enter category description (optional)"
              rows={4}
              showCount
              maxLength={500}
            />
          </Form.Item>

          <Form.Item
            label={
              <span>
                Category Icon <span className="text-red-500">*</span>
              </span>
            }
            validateStatus={imageError ? "error" : ""}
            help={
              imageError ||
              "Click to upload or drag and drop. Max. File Size: 2MB"
            }
          >
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                imageError
                  ? "border-red-300 hover:border-red-400"
                  : "border-blue-300 hover:border-blue-400"
              }`}
            >
              <Upload
                name="image"
                listType="picture-card"
                className="category-uploader w-full"
                showUploadList={false}
                beforeUpload={handleBeforeUpload}
              >
                {imageUrl ? (
                  <div className="relative inline-block">
                    {imageLoading && (
                      <Skeleton.Image
                        active
                        className="w-24 h-24 rounded-lg"
                        style={{ width: 96, height: 96 }}
                      />
                    )}
                    <img
                      src={imageUrl}
                      alt="category"
                      className={`w-24 h-24 rounded-lg object-cover border border-gray-200 ${
                        imageLoading ? "hidden" : "block"
                      }`}
                      onLoad={handleImageLoad}
                      onError={handleImageError}
                    />
                    {!imageLoading && (
                      <Button
                        type="text"
                        shape="circle"
                        icon={<DeleteOutlined />}
                        onClick={handleRemoveImage}
                        className="absolute -top-2 -right-2 bg-white shadow-md hover:bg-red-50"
                      />
                    )}
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center">
                    <PlusOutlined className="text-2xl mb-2 text-gray-500" />
                    <div className="text-sm text-gray-600">
                      Click to Upload or drag and drop
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Max. File Size: 2MB
                    </div>
                  </div>
                )}
              </Upload>
            </div>
          </Form.Item>

          <div className="flex justify-end gap-3 mt-8">
            <Button onClick={handleCancel} size="large" className="px-6">
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={isSubmitting || isLoading}
              className="bg-orange-500 border-orange-500 hover:bg-orange-600 px-6 rounded-lg"
            >
              {isSubmitting || isLoading ? "Updating..." : "Save"}
            </Button>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};

export default EditCategoryModal;
